/**
 * ListTable — canonical virtualized table (extracted from Items ItemTable).
 * All entity lists instantiate this component; do not fork table chrome.
 *
 * A ranked list (the item pool) is the same table in the order it is given:
 * a rank cell before the columns, rows that drag into a new order, and one
 * thing drawn between two rows (the cut line). Its rows are not virtualized —
 * a dragged row and the cut line both measure the rows as laid out — and its
 * headers do not sort, because the rank is the order.
 *
 * A row may open a peek (renderPeek): a chevron before the row opens a few
 * facts under it and a link to the record's own page, so a person can read a
 * little more before going in. One row's peek is open at a time.
 */

import { ObjectRef } from '@/components/ds/ObjectRef';
import type { ObjectKind } from '@/components/ds/objectKinds';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import { Checkbox } from '../ds';

export type ListTableDensity = 'compact' | 'default' | 'comfortable';

export type ListTableSortSpec = { field: string; direction: 'asc' | 'desc' };

export interface ListTableColumnDef<T> {
  key: string;
  label: string;
  sortField?: string | false;
  defaultWidth?: number;
  width?: string;
  align?: 'left' | 'right' | 'center';
  cellExtra?: string;
  /** Grow into remaining row width; shrink first (to defaultWidth as floor) when tight. */
  flex?: boolean;
  render: (item: T) => ReactNode;
}

export interface ListTableGroupedRows<T> {
  key: string;
  label: string;
  /** What kind of thing the group is (a category, a collection…); the header is marked with it. */
  kind?: ObjectKind;
  count: number;
  rows: T[];
}

const ROW_HEIGHTS: Record<ListTableDensity, number> = {
  compact: 36,
  default: 52,
  comfortable: 72,
};

const GROUP_HEADER_HEIGHT = 40;

/** A ranked list: rows in the order given, with a rank cell, drag, and a line between two rows. */
export interface ListTableRanked<T> {
  /** The leading cell: the row's rank. */
  renderRank: (item: T) => ReactNode;
  /** Drag a row onto another to reorder. Absent → no handles are drawn (the order cannot change here). */
  onReorder?: (activeId: string, overId: string) => void;
  /** Drawn between two rows (the cut line): before row `index`; rows.length = after the last. */
  divider?: { index: number; node: ReactNode };
  /** Drawn over the rows, positioned against them (the line that follows a dragged cut line). */
  overlay?: ReactNode;
  /** The element holding the rows. Each row in it carries `data-list-row`, for measuring. */
  rowsRef?: RefObject<HTMLDivElement | null>;
}

export interface ListTableProps<T> {
  rows: T[];
  getId: (row: T) => string;
  columns: ListTableColumnDef<T>[];
  density?: ListTableDensity;
  selectedIds: Set<string>;
  onToggleRow: (id: string) => void;
  onSelectAll: (ids: string[]) => void;
  /**
   * Select or clear several rows at once without touching the rest of the
   * selection. When given, each group header carries a checkbox that selects
   * the whole group (indeterminate while only some of it is selected).
   */
  onSelectRows?: (ids: string[], selected: boolean) => void;
  onClearSelection: () => void;
  onRowClick: (item: T) => void;
  sort: ListTableSortSpec[];
  onSortChange: (specs: ListTableSortSpec[]) => void;
  selectedRowId?: string | null;
  grouped?: ListTableGroupedRows<T>[] | null;
  expandedGroups?: Set<string>;
  onToggleGroup?: (key: string) => void;
  searchQuery?: string;
  columnWidths?: Record<string, number>;
  onColumnResize?: (key: string, width: number) => void;
  onColumnResetWidth?: (key: string) => void;
  /** Optional trailing actions column (Items row menu). */
  renderRowActions?: (item: T) => ReactNode;
  /** Extra classes on a cell wrapper (e.g. inline-edit ring). */
  getCellClassName?: (item: T, col: ListTableColumnDef<T>) => string | undefined;
  /** When false, hides checkbox column (read-only lists). Default true. */
  selectable?: boolean;
  /** Override row height per item (e.g. expandable rows). */
  getRowHeight?: (item: T) => number;
  /** Width of the trailing actions column. Default 40. */
  rowActionsWidth?: number;
  /** Draw the rows as a ranked list (see ListTableRanked). */
  ranked?: ListTableRanked<T>;
  /** A row's peek: drawn under it when its chevron is opened. Not drawn on a ranked list. */
  renderPeek?: (item: T) => ReactNode;
}

const PEEK_TOGGLE_WIDTH = 32;

function ColumnHeader({
  field,
  label,
  className,
  style,
  sort,
  onSort,
  isResizable,
  isResizing,
  onResizeStart,
  onResetWidth,
}: {
  field: string;
  label: string;
  className?: string;
  style?: CSSProperties;
  sort: ListTableSortSpec[];
  onSort: (field: string) => void;
  isResizable?: boolean;
  isResizing?: boolean;
  onResizeStart?: (e: React.MouseEvent) => void;
  onResetWidth?: () => void;
}) {
  const spec = sort.find((s) => s.field === field);
  return (
    <div className={`relative flex-shrink-0 ${className ?? ''}`} style={style}>
      {field ? (
        <button
          type="button"
          onClick={() => onSort(field)}
          className="flex items-center gap-1 px-2 py-2.5 hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)] transition-colors w-full min-w-0"
        >
          <span className="truncate">{label}</span>
          {spec ? (
            <span className="text-[var(--text-ter)] flex-shrink-0">
              {spec.direction === 'asc' ? (
                <ArrowUp className="h-3 w-3" strokeWidth={2.5} />
              ) : (
                <ArrowDown className="h-3 w-3" strokeWidth={2.5} />
              )}
            </span>
          ) : null}
        </button>
      ) : (
        // A column that does not sort here is a label, not a button that does nothing.
        // normal-case: a sort button does not take the row's uppercase (a button resets
        // text-transform), so a plain label matches it rather than shouting beside it.
        <span className="flex items-center px-2 py-2.5 min-w-0 normal-case">
          <span className="truncate">{label}</span>
        </span>
      )}
      {isResizable && (
        <div
          className={`absolute right-0 top-0 bottom-0 w-3 z-10 cursor-col-resize select-none flex items-stretch ${
            isResizing ? 'bg-[var(--slate-dim)]/60' : 'hover:bg-[var(--slate-dim)]'
          }`}
          onMouseDown={onResizeStart}
          onDoubleClick={onResetWidth}
          title="Drag to resize · Double-click to reset"
        >
          <div
            className={`w-px self-stretch transition-colors ${
              isResizing ? 'bg-[var(--slate)]' : 'bg-[var(--hover-tint)] group-hover/resize:bg-[var(--slate)]'
            }`}
            style={{ marginLeft: 'auto' }}
          />
        </div>
      )}
    </div>
  );
}

export function ListTable<T>({
  rows,
  getId,
  columns,
  density = 'default',
  selectedIds,
  onToggleRow,
  onSelectAll,
  onSelectRows,
  onClearSelection,
  onRowClick,
  sort,
  onSortChange,
  selectedRowId,
  grouped = null,
  expandedGroups = new Set(),
  onToggleGroup,
  searchQuery = '',
  columnWidths,
  onColumnResize,
  onColumnResetWidth,
  renderRowActions,
  getCellClassName,
  selectable = true,
  getRowHeight,
  rowActionsWidth = 40,
  ranked,
  renderPeek,
}: ListTableProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null);
  const rowHeight = ROW_HEIGHTS[density];
  const resizeDragRef = useRef<{ key: string; startX: number; startWidth: number } | null>(null);
  const [resizingKey, setResizingKey] = useState<string | null>(null);
  const showActions = Boolean(renderRowActions);
  const checkboxWidth = selectable ? 40 : 0;
  const rankWidth = ranked ? (ranked.onReorder ? 72 : 52) : 0;
  const peekable = Boolean(renderPeek) && !ranked;
  const peekWidth = peekable ? PEEK_TOGGLE_WIDTH : 0;
  const [peekId, setPeekId] = useState<string | null>(null);

  const virtualItems = useMemo(() => {
    if (!grouped || ranked) {
      return rows.map((r) => ({ type: 'row' as const, item: r }));
    }
    const result: Array<{ type: 'group'; group: ListTableGroupedRows<T> } | { type: 'row'; item: T }> = [];
    for (const group of grouped) {
      result.push({ type: 'group', group });
      if (expandedGroups.has(group.key)) {
        for (const item of group.rows) {
          result.push({ type: 'row', item });
        }
      }
    }
    return result;
  }, [rows, grouped, expandedGroups, ranked]);

  const virtualizer = useVirtualizer({
    // A ranked list draws every row in flow; the virtualizer has nothing to do.
    count: ranked ? 0 : virtualItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => {
      const entry = virtualItems[index];
      if (entry.type === 'group') return GROUP_HEADER_HEIGHT;
      if (getRowHeight) return getRowHeight(entry.item);
      return rowHeight;
    },
    overscan: 20,
  });

  // A closed peek gives its height back: drop the measured sizes so every row is estimated again.
  useEffect(() => {
    virtualizer.measure();
  }, [peekId, virtualizer]);

  const highlightMatch = useCallback(
    (text: string | undefined) => {
      if (!text) return null;
      if (!searchQuery) return text;
      const idx = text.toLowerCase().indexOf(searchQuery.toLowerCase());
      if (idx === -1) return text;
      return (
        <>
          {text.slice(0, idx)}
          <mark className="bg-[var(--mustard-dim)] text-[var(--mustard)] rounded px-0.5">
            {text.slice(idx, idx + searchQuery.length)}
          </mark>
          {text.slice(idx + searchQuery.length)}
        </>
      );
    },
    [searchQuery],
  );

  const handleColumnSort = useCallback(
    (field: string) => {
      const existing = sort.find((s) => s.field === field);
      if (existing) {
        if (existing.direction === 'desc') {
          onSortChange([{ field, direction: 'asc' }]);
        } else {
          onSortChange(sort.filter((s) => s.field !== field));
        }
      } else {
        onSortChange([{ field, direction: 'desc' }]);
      }
    },
    [sort, onSortChange],
  );

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent, key: string, defaultWidth: number) => {
      e.preventDefault();
      e.stopPropagation();
      const startWidth = columnWidths?.[key] ?? defaultWidth;
      resizeDragRef.current = { key, startX: e.clientX, startWidth };
      setResizingKey(key);

      const onMouseMove = (ev: MouseEvent) => {
        if (!resizeDragRef.current) return;
        const delta = ev.clientX - resizeDragRef.current.startX;
        const newWidth = Math.max(60, resizeDragRef.current.startWidth + delta);
        onColumnResize?.(resizeDragRef.current.key, newWidth);
      };

      const onMouseUp = () => {
        resizeDragRef.current = null;
        setResizingKey(null);
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [columnWidths, onColumnResize],
  );

  const getColStyle = useCallback(
    (col: ListTableColumnDef<T>): CSSProperties | undefined => {
      if (col.defaultWidth === undefined) return undefined;
      const manuallyResized = columnWidths?.[col.key] !== undefined;
      const w = columnWidths?.[col.key] ?? col.defaultWidth;
      // Once a user drags a flex column to a specific width, respect that
      // exactly (fixed) rather than having it keep growing back — manual
      // resize overrides the default fill behavior.
      if (col.flex && !manuallyResized) {
        return { width: w, flexGrow: 1, flexShrink: 1, minWidth: w };
      }
      return { width: w, flexShrink: 0, flexGrow: 0, minWidth: 0 };
    },
    [columnWidths],
  );

  const totalWidth = useMemo(() => {
    const colsWidth = columns.reduce(
      (sum, col) => sum + (columnWidths?.[col.key] ?? col.defaultWidth ?? 0),
      0,
    );
    return checkboxWidth + peekWidth + rankWidth + colsWidth + (showActions ? rowActionsWidth : 0);
  }, [columns, columnWidths, showActions, checkboxWidth, peekWidth, rankWidth, rowActionsWidth]);

  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.has(getId(r)));
  const someSelected = rows.some((r) => selectedIds.has(getId(r)));

  const rowClassName = (id: string) =>
    `flex items-center overflow-hidden border-b border-[var(--rule)]/60 transition-all duration-100 cursor-pointer group ${
      selectedRowId === id
        ? 'bg-[var(--slate-dim)] border-l-2 border-l-indigo-500'
        : selectedIds.has(id)
          ? 'bg-[var(--slate-dim)]/50 border-l-2 border-l-indigo-400'
          : 'hover:bg-[var(--surface)]/50 dark:hover:bg-[var(--hover-tint)] border-l-2 border-l-transparent'
    }`;

  /** One row's cells — the same for a virtualized row and a ranked one. `handle` is a ranked row's drag grip. */
  const renderCells = (item: T, handle?: ReactNode) => {
    const id = getId(item);
    const isSelected = selectedIds.has(id);
    const multilineRow = (getRowHeight?.(item) ?? rowHeight) > rowHeight;
    return (
      <>
        {selectable ? (
          <div className="w-10 flex-shrink-0 flex items-center justify-center">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                onToggleRow(id);
              }}
              onClick={(e) => e.stopPropagation()}
              className={`rounded border-[var(--rule)] text-[var(--text-ter)] focus:ring-indigo-500 h-3.5 w-3.5 transition-opacity ${
                isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
              }`}
            />
          </div>
        ) : null}

        {peekable ? (
          <div className="flex flex-shrink-0 items-center justify-center" style={{ width: peekWidth }}>
            <button
              type="button"
              className="p-1 text-[var(--text-ter)] hover:text-[var(--text-pri)]"
              aria-expanded={peekId === id}
              aria-label={peekId === id ? 'Close the peek' : 'Peek at this row'}
              onClick={(e) => {
                e.stopPropagation();
                setPeekId((cur) => (cur === id ? null : id));
              }}
            >
              {peekId === id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
          </div>
        ) : null}

        {ranked ? (
          <div className="flex flex-shrink-0 items-center gap-1 px-2" style={{ width: rankWidth }}>
            {handle}
            {ranked.renderRank(item)}
          </div>
        ) : null}

        {columns.map((col) => {
          const colStyle = getColStyle(col);
          const content = col.render(item);
          const text = typeof content === 'string' || typeof content === 'number' ? String(content) : null;
          const wrapped =
            text != null
              ? (
                  <span className="block truncate min-w-0 w-full" title={text}>
                    {highlightMatch(text)}
                  </span>
                )
              : content;
          return (
            <div
              key={col.key}
              className={`min-w-0 overflow-hidden ${colStyle ? '' : col.width ?? ''} px-2 ${col.cellExtra ?? ''} ${getCellClassName?.(item, col) ?? ''} ${
                density === 'compact' ? 'text-xs' : 'text-sm'
              } text-[var(--text-pri)] dark:text-[var(--text-pri)]`}
              style={colStyle}
            >
              <div
                className={`min-w-0 w-full overflow-hidden ${multilineRow ? '' : 'truncate'} [&_*]:max-w-none`}
              >
                {wrapped}
              </div>
            </div>
          );
        })}

        {showActions && (
          <div className="flex-shrink-0 flex items-center justify-end gap-0.5 pr-2" style={{ width: rowActionsWidth }}>
            {renderRowActions!(item)}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="flex-1 overflow-hidden min-h-0">
      {/* A ranked list measures its visible width so the line between rows stays in view when the columns scroll sideways. */}
      <div ref={parentRef} className={`h-full overflow-auto ${ranked ? '[container-type:inline-size]' : ''}`}>
        <div style={{ minWidth: totalWidth }}>
          <div
            className="sticky top-0 z-10 flex items-center bg-[var(--surface)]/70 dark:bg-[var(--surface)] border-b border-[var(--rule)]/80 dark:border-[var(--rule)] text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-dim)] uppercase tracking-wider"
          >
            {selectable ? (
              <div className="w-10 flex-shrink-0 flex items-center justify-center py-2.5">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={() =>
                    allSelected ? onClearSelection() : onSelectAll(rows.map((r) => getId(r)))
                  }
                  className="rounded border-[var(--rule)] text-[var(--text-ter)] focus:ring-indigo-500 h-3.5 w-3.5"
                />
              </div>
            ) : null}

            {peekable ? <div className="flex-shrink-0" style={{ width: peekWidth }} /> : null}

            {ranked ? (
              <div className="flex-shrink-0 px-2 py-2.5" style={{ width: rankWidth }}>
                Rank
              </div>
            ) : null}

            {columns.map((col) => {
              const colStyle = getColStyle(col);
              const isResizable = col.defaultWidth !== undefined;
              // A ranked list is in rank order; its headers do not sort.
              const sortField = ranked || col.sortField === false ? '' : (col.sortField ?? col.key);
              return (
                <ColumnHeader
                  key={col.key}
                  field={sortField}
                  label={col.label}
                  className={colStyle ? undefined : col.width}
                  style={colStyle}
                  sort={sort}
                  onSort={handleColumnSort}
                  isResizable={isResizable}
                  isResizing={resizingKey === col.key}
                  onResizeStart={
                    isResizable
                      ? (e) => handleResizeMouseDown(e, col.key, col.defaultWidth!)
                      : undefined
                  }
                  onResetWidth={
                    isResizable ? () => onColumnResetWidth?.(col.key) : undefined
                  }
                />
              );
            })}

            {showActions && <div className="flex-shrink-0" style={{ width: rowActionsWidth }} />}
          </div>

          {ranked ? (
            <RankedRows
              rows={rows}
              getId={getId}
              ranked={ranked}
              rowClassName={rowClassName}
              rowHeight={rowHeight}
              getRowHeight={getRowHeight}
              onRowClick={onRowClick}
              renderCells={renderCells}
            />
          ) : (
          <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
            {virtualizer.getVirtualItems().map((vi) => {
              const entry = virtualItems[vi.index];

              if (entry.type === 'group') {
                const { group } = entry;
                const isExpanded = expandedGroups.has(group.key);
                const groupIds = group.rows.map((r) => getId(r));
                const groupSelectedCount = groupIds.filter((gid) => selectedIds.has(gid)).length;
                const groupAllSelected = groupIds.length > 0 && groupSelectedCount === groupIds.length;
                const showGroupCheckbox = selectable && Boolean(onSelectRows) && groupIds.length > 0;
                return (
                  <div
                    key={`group-${group.key}`}
                    style={{
                      position: 'absolute',
                      top: 0,
                      transform: `translateY(${vi.start}px)`,
                      height: GROUP_HEADER_HEIGHT,
                      width: '100%',
                    }}
                    className="flex items-stretch bg-[var(--surface)] dark:bg-[var(--surface)] border-b border-[var(--rule)]/60 dark:border-[var(--rule)]"
                  >
                    {showGroupCheckbox ? (
                      <div className="w-10 flex-shrink-0 flex items-center justify-center">
                        <Checkbox
                          aria-label={`Select all ${groupIds.length} in ${group.label}`}
                          title={groupAllSelected ? `Clear the ${groupIds.length} in this group` : `Select all ${groupIds.length} in this group`}
                          checked={groupAllSelected}
                          indeterminate={groupSelectedCount > 0 && !groupAllSelected}
                          onChange={() => onSelectRows?.(groupIds, !groupAllSelected)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => onToggleGroup?.(group.key)}
                      className={`flex items-center gap-2 flex-1 min-w-0 h-full ${showGroupCheckbox ? 'pr-4' : 'px-4'} hover:bg-[var(--hover-tint)]/80 dark:hover:bg-[var(--hover-tint)] transition-colors`}
                    >
                      <div
                        className={`transition-transform duration-150 ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
                      >
                        <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                      </div>
                      <span className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                        {group.kind ? <ObjectRef kind={group.kind} name={group.label} /> : group.label}
                      </span>
                      <span className="text-[10px] text-[var(--text-ter)] font-medium tabular-nums bg-[var(--hover-tint)]/60 px-1.5 py-0.5">
                        {group.count}
                      </span>
                    </button>
                  </div>
                );
              }

              const item = entry.item;
              const id = getId(item);
              const resolvedRowHeight = getRowHeight?.(item) ?? rowHeight;

              if (peekable && peekId === id) {
                // The open row grows by its peek; the virtualizer measures it so the rows below move down.
                return (
                  <div
                    key={id}
                    data-index={vi.index}
                    ref={virtualizer.measureElement}
                    style={{ position: 'absolute', top: 0, transform: `translateY(${vi.start}px)`, width: '100%' }}
                  >
                    <div style={{ height: resolvedRowHeight }} className={rowClassName(id)} onClick={() => onRowClick(item)}>
                      {renderCells(item)}
                    </div>
                    <div
                      className="border-b border-[var(--rule)] bg-[var(--surface)] px-4 py-3"
                      style={{ paddingLeft: checkboxWidth + peekWidth + 8 }}
                      data-testid="list-row-peek"
                    >
                      {renderPeek!(item)}
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={id}
                  style={{
                    position: 'absolute',
                    top: 0,
                    transform: `translateY(${vi.start}px)`,
                    height: resolvedRowHeight,
                    width: '100%',
                  }}
                  className={rowClassName(id)}
                  onClick={() => onRowClick(item)}
                >
                  {renderCells(item)}
                </div>
              );
            })}
          </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Ranked rows — every row in flow, in the order given, optionally draggable
// ============================================================================

interface RankedRowsProps<T> {
  rows: T[];
  getId: (row: T) => string;
  ranked: ListTableRanked<T>;
  rowClassName: (id: string) => string;
  rowHeight: number;
  getRowHeight?: (item: T) => number;
  onRowClick: (item: T) => void;
  renderCells: (item: T, handle?: ReactNode) => ReactNode;
}

function RankedRows<T>({ rows, getId, ranked, rowClassName, rowHeight, getRowHeight, onRowClick, renderCells }: RankedRowsProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = useMemo(() => rows.map(getId), [rows, getId]);
  const { onReorder, divider } = ranked;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onReorder?.(String(active.id), String(over.id));
  };

  // The divider spans the visible width and stays put while the columns
  // scroll sideways, so its handle is never off to one side.
  const dividerNode = divider ? <div className="sticky left-0 w-[100cqw]">{divider.node}</div> : null;

  const body = (
    <div ref={ranked.rowsRef} className="relative">
      {ranked.overlay}
      {rows.map((item, index) => {
        const id = getId(item);
        return (
          <Fragment key={id}>
            {divider?.index === index && dividerNode}
            <RankedRow
              id={id}
              draggable={Boolean(onReorder)}
              className={rowClassName(id)}
              height={getRowHeight?.(item) ?? rowHeight}
              onClick={() => onRowClick(item)}
            >
              {(handle) => renderCells(item, handle)}
            </RankedRow>
          </Fragment>
        );
      })}
      {divider && divider.index >= rows.length && dividerNode}
    </div>
  );

  if (!onReorder) return body;
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {body}
      </SortableContext>
    </DndContext>
  );
}

function RankedRow({
  id,
  draggable,
  className,
  height,
  onClick,
  children,
}: {
  id: string;
  draggable: boolean;
  className: string;
  height: number;
  onClick: () => void;
  children: (handle: ReactNode) => ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !draggable });
  const handle = draggable ? (
    <button
      type="button"
      {...attributes}
      {...listeners}
      onClick={(e) => e.stopPropagation()}
      aria-label="Drag to reorder"
      title="Drag to reorder"
      className="flex-shrink-0 cursor-grab text-[var(--text-ter)] hover:text-[var(--text-sec)] active:cursor-grabbing"
    >
      <GripVertical className="h-4 w-4" />
    </button>
  ) : null;
  return (
    <div
      ref={setNodeRef}
      data-list-row
      style={{ height, transform: CSS.Transform.toString(transform), transition }}
      className={`${className} ${isDragging ? 'relative z-20 opacity-60' : ''}`}
      onClick={onClick}
    >
      {children(handle)}
    </div>
  );
}
